import type {TicketSla} from '../types/ticket';

/**
 * How an SLA reads in the queue. The state drives both the glyph and its colour,
 * so breach / at-risk / on-track stay distinguishable without colour — colour must
 * never be the only carrier.
 */
export type SlaState = 'none' | 'paused' | 'breached' | 'atRisk' | 'onTrack';

/** Fraction of the threshold that must elapse before an SLA reads as at-risk. */
const AT_RISK_RATIO = 0.8;

export interface SlaPresentation {
	state: SlaState;
	/** Design-system Icon glyph name. */
	icon: 'clock' | 'warning' | 'alert';
	/** Design-system Icon `newColor` key (a sys.color.iconfill key). */
	iconColor: 'success' | 'warning' | 'error' | 'disabled';
	/** Text shown beside the glyph, e.g. `Due in 1h 14m`, `Overdue 20m`. */
	text: string;
	/**
	 * The same reading with the framing words dropped — `1h 14m`, `20m`, `Paused`.
	 * For places already headed by the ticket itself (the detail header, the Details
	 * panel), where "Due in" is understood and the row is tight. The glyph and colour
	 * still carry the state, so this never stands alone.
	 */
	compactText: string;
}

/**
 * Duration in the queue's compact form: `2d 3h`, `4h 30m`, `12m`. Rounds down, and
 * never returns an empty string — under a minute reads `0m` so a cell always has a
 * value.
 */
export function formatSlaDuration(seconds: number): string {
	const total = Math.max(0, Math.floor(seconds));
	const days = Math.floor(total / 86400);
	const hours = Math.floor((total % 86400) / 3600);
	const minutes = Math.floor((total % 3600) / 60);
	if (days > 0) return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
	if (hours > 0) return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
	return `${minutes}m`;
}

/**
 * Derive the queue's SLA reading from the ticket's SLA record.
 *
 * The record carries elapsed and threshold seconds, not a remaining duration, so
 * remaining is derived here — one helper so the list column, the board card and
 * the detail header always say the same thing.
 *
 * - paused → a stopped clock, greyed, `SLA paused`
 * - breached → alert glyph, `Overdue <overrun>`
 * - at-risk (≥80% of the threshold elapsed) → warning glyph, `Due in <remaining>`
 * - on-track → clock glyph, `Due in <remaining>`
 */
export function slaPresentation(sla: TicketSla | null | undefined): SlaPresentation {
	if (!sla) {
		return {state: 'none', icon: 'clock', iconColor: 'disabled', text: '—', compactText: '—'};
	}
	if (sla.paused) {
		return {state: 'paused', icon: 'clock', iconColor: 'disabled', text: 'SLA paused', compactText: 'Paused'};
	}
	const remaining = sla.thresholdSeconds - sla.elapsedSeconds;
	if (sla.breached || remaining <= 0) {
		const overrun = formatSlaDuration(-remaining);
		return {state: 'breached', icon: 'alert', iconColor: 'error', text: `Overdue ${overrun}`, compactText: `-${overrun}`};
	}
	const left = formatSlaDuration(remaining);
	const ratio = sla.thresholdSeconds > 0 ? sla.elapsedSeconds / sla.thresholdSeconds : 0;
	if (ratio >= AT_RISK_RATIO) {
		return {state: 'atRisk', icon: 'warning', iconColor: 'warning', text: `Due in ${left}`, compactText: left};
	}
	return {state: 'onTrack', icon: 'clock', iconColor: 'success', text: `Due in ${left}`, compactText: left};
}
